import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('skipya-theme');
    if (saved) return saved;
    // Then check system preference
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    localStorage.setItem('skipya-theme', theme);
    // Update document class for global styling
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const colors = {
    dark: {
      bg: {
        primary: '#0F0F1A',
        secondary: '#1A1A2E',
        tertiary: '#252540',
        glass: 'rgba(255, 255, 255, 0.05)',
        glassHover: 'rgba(255, 255, 255, 0.08)',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#94A3B8',
        muted: '#64748B',
      },
      border: 'rgba(255, 255, 255, 0.08)',
      borderHover: 'rgba(255, 255, 255, 0.15)',
    },
    light: {
      bg: {
        primary: '#FAFBFC',
        secondary: '#FFFFFF',
        tertiary: '#F1F5F9',
        glass: 'rgba(255, 255, 255, 0.8)',
        glassHover: 'rgba(255, 255, 255, 0.95)',
      },
      text: {
        primary: '#0F172A',
        secondary: '#475569',
        muted: '#94A3B8',
      },
      border: 'rgba(0, 0, 0, 0.08)',
      borderHover: 'rgba(0, 0, 0, 0.15)',
    },
  };

  const accent = {
    primary: '#81d8d0',
    primaryHover: '#6bc4bc',
    secondary: '#4ECDC4',
    danger: '#FF6B6B',
    warning: '#FBBF24',
    success: '#10B981',
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme, 
      colors: colors[theme],
      accent,
      isDark: theme === 'dark',
      isLight: theme === 'light',
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
